import type React from 'react';
import { useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { tokens } from '../../design-system/tokens';
import { STATUS_PILL_DEFAULT_MAX_WIDTH, STATUS_PILL_TEXT_TRUNCATE, type UserStatus } from './types';
import { useInterval } from './useInterval';

export interface StatusPillProps {
  readonly status: UserStatus;
  readonly now?: Date;
  readonly showsCountdown?: boolean;
  readonly maxWidth?: number;
}

export const StatusPill: React.FC<StatusPillProps> = ({
  status,
  now,
  showsCountdown = true,
  maxWidth = STATUS_PILL_DEFAULT_MAX_WIDTH,
}) => {
  const scheme = useColorScheme();
  const colorScheme = scheme === 'dark' ? 'dark' : 'light';
  const colors = tokens.color.semantic[colorScheme];

  const [tick, setTick] = useState(now ?? new Date());
  const wantsCountdown =
    showsCountdown && status.variation === 'emojiTextDuration' && status.expiresAt !== undefined;

  useInterval(
    () => {
      setTick(new Date());
    },
    30_000,
    wantsCountdown && now === undefined,
  );

  if (status.variation === 'none') {
    return null;
  }

  const effectiveNow = now ?? tick;
  const truncatedText =
    status.text && status.text.length > STATUS_PILL_TEXT_TRUNCATE
      ? `${status.text.slice(0, STATUS_PILL_TEXT_TRUNCATE)}\u2026`
      : (status.text ?? '');

  const countdownLabel =
    wantsCountdown && status.expiresAt
      ? formatRemaining(status.expiresAt.getTime() - effectiveNow.getTime())
      : null;

  return (
    <View
      accessibilityRole="text"
      accessibilityLabel={`status ${status.variation}`}
      style={[
        styles.container,
        { maxWidth, backgroundColor: colors.surface.secondary, borderColor: colors.border.primary },
      ]}
    >
      {status.emoji ? (
        <Text style={styles.emoji} testID="status-pill-emoji">
          {status.emoji}
        </Text>
      ) : null}
      {truncatedText.length > 0 ? (
        <Text
          numberOfLines={1}
          ellipsizeMode="tail"
          style={[styles.text, { color: colors.text.primary }]}
          testID="status-pill-text"
        >
          {truncatedText}
        </Text>
      ) : null}
      {countdownLabel ? (
        <Text
          style={[styles.countdown, { color: colors.text.secondary }]}
          testID="status-pill-countdown"
        >
          {countdownLabel}
        </Text>
      ) : null}
    </View>
  );
};

export function formatRemaining(remainingMs: number): string {
  if (remainingMs <= 0) {
    return 'expired';
  }
  const totalMinutes = Math.floor(remainingMs / 60_000);
  if (totalMinutes < 60) {
    return `${totalMinutes}m`;
  }
  const totalHours = Math.floor(totalMinutes / 60);
  if (totalHours < 24) {
    return `${totalHours}h`;
  }
  const days = Math.floor(totalHours / 24);
  return `${days}d`;
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.xs,
    paddingVertical: 2,
    borderRadius: tokens.radius.pill,
    borderWidth: 1,
    gap: 4,
  },
  emoji: {
    fontSize: tokens.typography.fontSize.footnote,
  },
  text: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.caption,
    fontWeight: '500',
    flexShrink: 1,
  },
  countdown: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.caption,
    fontWeight: '500',
  },
});
